import { NotAvailable } from '@/components/blocks/NotAvailable';
import { CatalogEntryListCompareCollector } from '@/components/content/CatalogEntryList/parts/Compare/Collector';
import { CatalogEntryListFacetChips } from '@/components/content/CatalogEntryList/parts/FacetChips';
import { CatalogEntryListPagination } from '@/components/content/CatalogEntryList/parts/Pagination';
import { CatalogEntryListProductGrid } from '@/components/content/CatalogEntryList/parts/ProductGrid';
import { CatalogEntryListResultsHeader } from '@/components/content/CatalogEntryList/parts/ResultsHeader';
import { CatalogEntryListSort } from '@/components/content/CatalogEntryList/parts/Sort';
import { CatalogEntryListZeroResults } from '@/components/content/CatalogEntryList/parts/ZeroResults';
import {
	catalogEntryListContainerSX,
	titleAndSortContainerSx,
} from '@/components/content/CatalogEntryList/styles/container';
import { SLPLPProductCards } from '@/components/skeleton/components/elements/PLP';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useCompareCollectorV2 } from '@/data/Content/CompareCollectorV2';
import { useLayout } from '@/data/Layout';
import { useLocalization } from '@/data/Localization';
import { ITEM_LIST_IDS } from '@/data/constants/gtm';
import { PAGE_TOKEN_NAME_CATEGORY } from '@/data/constants/layout';
import { ContentProvider } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { GTMContainerListType } from '@/data/types/GTM';
import { Box, Stack } from '@mui/material';
import {
	buildCategoryEntityViewName,
	useAdobeTargetEntityView,
} from 'presentation/events/hooks/adobe/useAdobeTargetEntityView';
import { FC, useMemo } from 'react';

export const CatalogEntryList: FC<{ id: ID }> = ({ id }) => {
	const catalogEntryList = useCatalogEntryList(id);
	const {
		filteredParams: { searchTerm },
		entitled,
		loading,
		total,
		searchSuggestions,
		breadCrumbTrailEntryView,
	} = catalogEntryList;

	useAdobeTargetEntityView(buildCategoryEntityViewName(breadCrumbTrailEntryView));
	const { searchSuggestion, suggestions } = searchSuggestions;
	const compare = useCompareCollectorV2(id);
	const { layout } = useLayout();
	const isCategoryPage = layout?.tokenName === PAGE_TOKEN_NAME_CATEGORY;
	const { compareEnabled, compareState } = compare;
	const { notAvailable } = useLocalization('Category');
	const { gtmItemList } = useLocalization('ProductGrid').Labels;
	const listData = useMemo(
		() =>
			!searchTerm
				? ({
						productListData: { listId: ITEM_LIST_IDS.ITEM_LIST, listName: gtmItemList.t() },
				  } as GTMContainerListType)
				: undefined,
		[gtmItemList, searchTerm]
	);

	const contextValue = useMemo(
		() => ({ ...catalogEntryList, ...compare, ...listData, searchSuggestion }),
		[catalogEntryList, compare, listData, searchSuggestion]
	);

	if (loading) return <SLPLPProductCards />;

	if (!entitled && !searchTerm && !total) return <NotAvailable message={notAvailable.t()} />;

	return (
		<ContentProvider value={contextValue}>
			<Stack
				spacing={2}
				sx={catalogEntryListContainerSX}
				id="catalog-entry-list-product-container"
				data-testid="catalog-entry-list-product-container"
			>
				{!total && searchTerm && searchTerm !== '' ? (
					<CatalogEntryListZeroResults
						searchTerm={searchTerm}
						searchSuggestion={searchSuggestion}
						suggestions={suggestions}
					/>
				) : null}

				{total ? (
					<>
						{searchSuggestion ? (
							<CatalogEntryListZeroResults
								searchTerm={searchTerm ?? ''}
								searchSuggestion={searchSuggestion}
								suggestions={suggestions}
							/>
						) : null}
						<Stack spacing={2} sx={titleAndSortContainerSx}>
							<Box>
								<CatalogEntryListResultsHeader isCategoryPage={isCategoryPage} />
							</Box>
							<CatalogEntryListSort />
						</Stack>
					</>
				) : null}
				<CatalogEntryListFacetChips />
				<CatalogEntryListProductGrid />
				<CatalogEntryListPagination />
			</Stack>
			{compareEnabled && compareState.len > 0 ? <CatalogEntryListCompareCollector /> : null}
		</ContentProvider>
	);
};
